import React, { useEffect, useState } from 'react';
import './styles.scss';

interface PapersRecord {
  type: 'citizenship' | 'passport';
  issuerId: string;
  holderName: string;
  issuedAt: number;
  genuine: boolean;
}

interface FactionSummary {
  id: string;
  name: string;
}

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('papers sendMessage', payload);
  }
};

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Own client is always the holder here - genuine is always shown, unlike
// the "montrer mes papiers" viewer flow where it's hidden unless the
// viewer is an officer of the issuing faction (see papersShow).
function cardClass(p: PapersRecord): string {
  if (p.type === 'passport' && p.issuerId === 'thalmor') return 'papers-card papers-card--thalmor';
  if (p.type === 'passport' && p.issuerId === 'empire') return 'papers-card papers-card--empire';
  return 'papers-card papers-card--citizenship';
}

const PapersMenu: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [papers, setPapers] = useState<PapersRecord[]>([]);
  const [factions, setFactions] = useState<FactionSummary[]>([]);
  const [issueId, setIssueId] = useState('');
  const [forgeId, setForgeId] = useState('');
  const [canForge, setCanForge] = useState(false);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { papers?: PapersRecord[]; factions?: FactionSummary[]; canForge?: boolean } | null;
      setPapers(detail?.papers ?? []);
      setFactions(detail?.factions ?? []);
      setCanForge(detail?.canForge ?? false);
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showPapersMenu', showHandler);
    window.addEventListener('skymp5-client:hidePapersMenu', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showPapersMenu', showHandler);
      window.removeEventListener('skymp5-client:hidePapersMenu', hideHandler);
    };
  }, []);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const requestIssue = () => {
    if (!issueId) return;
    send({ action: 'requestIssue', id: issueId });
  };

  const requestForge = () => {
    if (!forgeId) return;
    send({ action: 'requestForge', id: forgeId });
  };

  const label = (type: PapersRecord['type'], issuerId: string): string => {
    const issuerName = factions.find((f) => f.id === issuerId)?.name ?? issuerId;
    return type === 'citizenship' ? `Certificat de citoyenneté - ${issuerName}` : `Passeport - ${issuerName}`;
  };

  return (
    <div className="papers-overlay">
      <div className="papers-panel skyrim-panel">
        <div className="papers-header">
          <div className="papers-header-title">Mes papiers</div>
        </div>

        <div className="papers-body">
          <div className="papers-section">
            <div className="papers-heading">Documents possédés</div>
            {papers.length === 0 ? (
              <div className="papers-empty">Tu n'as aucun papier sur toi.</div>
            ) : (
              <div className="papers-list">
                {papers.map((p) => (
                  <div className={cardClass(p)} key={`${p.type}:${p.issuerId}`}>
                    <div className="papers-card-label">{label(p.type, p.issuerId)}</div>
                    <div className="papers-card-holder">{p.holderName}</div>
                    <div className="papers-card-date">Délivré le {formatDate(p.issuedAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="papers-section">
            <div className="papers-heading">Demander des papiers</div>
            <div className="papers-form-row">
              <select className="papers-select" value={issueId} onChange={(e) => setIssueId(e.target.value)}>
                <option value="">Choisir...</option>
                {factions.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <button type="button" className="papers-button" disabled={!issueId} onClick={requestIssue}>
                Demander
              </button>
            </div>
            <div className="papers-hint">
              Citoyenneté : il faut être physiquement dans le hold concerné. Passeport Thalmor : il faut être
              membre du Thalmor. Passeport Empire : aucune condition.
            </div>
          </div>

          {canForge && (
            <div className="papers-section">
              <div className="papers-heading papers-heading--danger">Fabriquer un faux</div>
              <div className="papers-form-row">
                <select className="papers-select" value={forgeId} onChange={(e) => setForgeId(e.target.value)}>
                  <option value="">Choisir...</option>
                  {factions.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <button type="button" className="papers-button papers-button--danger" disabled={!forgeId} onClick={requestForge}>
                  Fabriquer
                </button>
              </div>
              <div className="papers-hint">Réservé à la Guilde des Voleurs.</div>
            </div>
          )}
        </div>

        <div className="papers-close-row">
          <div className="papers-hint-kbd">
            <kbd>Échap</kbd> Fermer
          </div>
          <button type="button" className="papers-button papers-button--ghost" onClick={close}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PapersMenu;
